import { format } from "date-fns";
import { Button, Icon, Modal, Tooltip, Typography } from "@/components/atoms";
import { useProfile } from "@/hooks";

interface ExistingVehicleData {
  make: string;
  model: string;
  registration_number: string;
  year: number;
  color: string;
  engine_type: string;
  capacity_pax: number;
  state_uuid: string;
  registration_expiry_date: string | null;
  vin: string;
  vehicle_category?: { uuid: string; name: string } | null;
}

interface PlateAlreadyLinkedModalProps {
  open: boolean;
  existingVehicle?: ExistingVehicleData;
  onClose: () => void;
}

export const PlateAlreadyLinkedModal = ({ open, existingVehicle, onClose }: PlateAlreadyLinkedModalProps) => {
  const { dateFormat } = useProfile();

  if (!existingVehicle) return null;

  return (
    <Modal open={open} onClose={onClose} className="w-[85vw] !p-6 md:w-[550px]">
      <div className="flex flex-col">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-warning/20">
          <Icon name="Danger" variant="Bold" size="sm" className="text-warning" />
        </div>

        <Typography variant="title" className="mt-4">
          Plate Number Already Linked to Another Vehicle
        </Typography>
        <Typography variant="paragraph" className="mt-2 text-neutral-dark-gray">
          The plate number you entered is already registered to another vehicle in the system. The details of that vehicle are shown below
          for your reference
        </Typography>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <DetailRow label="Make" value={existingVehicle.make} />
          <DetailRow label="Model" value={existingVehicle.model} />
          <DetailRow label="Plate Number" value={existingVehicle.registration_number} />
          <DetailRow label="Year" value={existingVehicle.year?.toString() ?? "-"} />
          <DetailRow
            label="Color"
            value={
              existingVehicle.color ? (
                <span className="inline-flex items-center gap-2 capitalize">
                  <span
                    className="inline-block h-3 w-3 rounded-full border border-black"
                    style={{ backgroundColor: existingVehicle.color.toLowerCase().replace("_", "") }}
                  />
                  {existingVehicle.color.toLowerCase().replace("_", " ")}
                </span>
              ) : (
                "-"
              )
            }
          />
          <DetailRow label="Engine Type" value={existingVehicle.engine_type?.toLowerCase() ?? "-"} />
          <DetailRow label="Passenger Capacity" value={`${existingVehicle.capacity_pax} pax`} />
          <DetailRow
            label="Registration Expiry"
            value={existingVehicle.registration_expiry_date ? format(new Date(existingVehicle.registration_expiry_date), dateFormat) : "-"}
          />
          <DetailRow label="VIN" value={existingVehicle.vin ?? "-"} />
          <DetailRow
            label={
              <span className="inline-flex items-center gap-1">
                Vehicle Category
                <Tooltip content="System-defined category based on vehicle make/model" placement="top">
                  <Icon name="InfoCircle" variant="Bold" className="text-neutral-dark-gray" size="sm" />
                </Tooltip>
              </span>
            }
            value={existingVehicle.vehicle_category?.name ?? "-"}
          />
        </div>

        <div className="mt-6 flex justify-end">
          <Button variant="secondary" size="md" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const DetailRow = ({ label, value }: { label: React.ReactNode; value: React.ReactNode }) => (
  <>
    <div className="col-span-1 flex items-center">
      <Typography className="text-neutral-dark-gray">{label}</Typography>
    </div>
    <div className="col-span-1 flex items-center justify-end">
      <Typography className="capitalize text-neutral-black">{value}</Typography>
    </div>
  </>
);
